import api from './apiClient';

const BASE_URL = '/checkout';

/**
 * Simplified checkout API for unseated events only.
 * Handles checkout processing for general admission tickets.
 */
export const checkoutApi = {
    /**
     * Complete checkout for an order (from cart-to-checkout flow)
     */
    completeCheckout: async (orderId, paymentDetails) => {
        try {
            if (!orderId) {
                throw new Error('Order ID is required');
            }
            if (!paymentDetails) {
                throw new Error('Payment details are required');
            }

            // Build payment request - amount is optional since backend calculates it from order
            const paymentRequest = {
                method: paymentDetails.method || 'card',
                currencyCode: paymentDetails.currencyCode || 'USD',
                referenceCode: paymentDetails.reference || 'test-payment'
            };

            // Only include amount if provided (for validation), otherwise backend calculates it
            if (paymentDetails.amount !== undefined && paymentDetails.amount !== null) {
                paymentRequest.amount = paymentDetails.amount;
            }

            const response = await api.post(`${BASE_URL}/${orderId}/complete`, paymentRequest);

            return {
                success: true,
                order: response.data.order,
                message: response.data.message || 'Checkout completed successfully'
            };
        } catch (error) {
            console.error('Error completing checkout:', error);
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Checkout failed',
                statusCode: error.response?.status
            };
        }
    },

    /**
     * Cancel a pending checkout order
     */
    cancelCheckout: async (orderId, reason = 'User cancelled') => {
        try {
            if (!orderId) {
                throw new Error('Order ID is required');
            }

            await api.post(`${BASE_URL}/${orderId}/cancel`, { reason });

            return {
                success: true,
                message: 'Checkout cancelled successfully'
            };
        } catch (error) {
            console.error('Error cancelling checkout:', error);
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Failed to cancel checkout',
                statusCode: error.response?.status
            };
        }
    },

    /**
     * Get checkout status for an order
     */
    getCheckoutStatus: async (orderId) => {
        try {
            if (!orderId) {
                throw new Error('Order ID is required');
            }

            const response = await api.get(`${BASE_URL}/${orderId}/status`);

            return {
                success: true,
                status: response.data,
                message: 'Status retrieved successfully'
            };
        } catch (error) {
            console.error('Error getting checkout status:', error);
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Failed to get checkout status',
                statusCode: error.response?.status
            };
        }
    },

    /**
     * Legacy method - not supported in unseated MVP
     */
    prepareCheckout: async () => {
        throw new Error('Seated checkout is not supported in unseated-only MVP. Use cart-to-checkout flow instead.');
    },

    /**
     * Legacy method - not supported in unseated MVP
     */
    prepareDirectCheckout: async () => {
        throw new Error('Direct seated checkout is not supported in unseated-only MVP. Use cart-to-checkout flow instead.');
    }
};

export default checkoutApi;
